import { type ActorRefFrom, setup } from 'xstate';

import { getReorderEventParams } from './helpers';
import { initialContext } from './Reorder.machine.constants';
import { ReorderMachineDelays } from './Reorder.machine.delays';
import { Actions, Guards } from './setup';
import { type Context, type Event } from './types';

export { validReorderEventKeys } from './Reorder.machine.constants';
export type { ReorderEvent, ReorderEventKey, ReorderStep } from './types';

// ─────────────────────────────────────────────────────────────────────────────

const model = setup({
  types: {
    context: {} as Context,
    events: {} as Event,
  },
  delays: ReorderMachineDelays,
  guards: {
    checkIfCanCancelStep: Guards.checkIfCanCancelStep,
    checkIfGroupOrderReorderStartedFromLocationSearch:
      Guards.checkIfGroupOrderReorderStartedFromLocationSearch,
  },
  actions: {
    storeOrderDetails: Actions.storeOrderDetails,
    storeDeliveryDetails: Actions.storeDeliveryDetails,
    storeResponse: Actions.storeResponse,
    setReordering: Actions.setReordering,
    setPreviousStep: Actions.setPreviousStep,
    storeGroupOrderReorderRequest: Actions.storeGroupOrderReorderRequest,
    clearGroupOrderReorderRequest: Actions.clearGroupOrderReorderRequest,
    resetContext: Actions.resetContext,
  },
});

export const reorderMachine = model.createMachine({
  id: 'sgReorderMachine',
  context: initialContext,
  initial: 'idle',
  states: {
    idle: {
      on: {
        REQUEST_GROUP_ORDER_REORDER_CONFIRMATION: {
          target: 'confirmingGroupOrderReorder',
          actions: { type: 'storeGroupOrderReorderRequest', params: ({ event }) => event },
        },
        ACTIVE_BAG_WARNING: {
          target: 'activeBagWarning',
          actions: [
            { type: 'storeOrderDetails', params: getReorderEventParams },
            { type: 'setPreviousStep', params: { previousStep: undefined } },
          ],
        },
        CHANGE_LOCATION: {
          target: 'locationSearch',
          actions: [
            { type: 'storeOrderDetails', params: getReorderEventParams },
            { type: 'setPreviousStep', params: { previousStep: undefined } },
          ],
        },
        CONFIRM_LOCATION: {
          target: 'locationConfirmation',
          actions: [
            { type: 'storeOrderDetails', params: getReorderEventParams },
            { type: 'setPreviousStep', params: { previousStep: undefined } },
          ],
        },
        CONFLICT_REVIEW: {
          target: 'conflictReview',
          actions: [
            { type: 'storeOrderDetails', params: getReorderEventParams },
            { type: 'storeResponse', params: getReorderEventParams },
            { type: 'setReordering', params: { reordering: false } },
            { type: 'setPreviousStep', params: { previousStep: undefined } },
          ],
        },
        SET_REORDERING: {
          target: 'locationSearch',
          actions: [
            { type: 'storeOrderDetails', params: getReorderEventParams },
            { type: 'storeResponse', params: getReorderEventParams },
            { type: 'setPreviousStep', params: { previousStep: undefined } },
            { type: 'setReordering', params: { reordering: true } },
          ],
        },
      },
    },
    locationSearch: {
      on: {
        REQUEST_GROUP_ORDER_REORDER_CONFIRMATION: {
          target: 'confirmingGroupOrderReorder',
          actions: { type: 'storeGroupOrderReorderRequest', params: ({ event }) => event },
        },
        CONFIRM_LOCATION: {
          target: 'locationConfirmation',
          actions: { type: 'setPreviousStep', params: { previousStep: undefined } },
        },
        CONFLICT_REVIEW: {
          target: 'conflictReview',
          actions: [
            { type: 'storeOrderDetails', params: getReorderEventParams },
            { type: 'storeDeliveryDetails', params: getReorderEventParams },
            { type: 'storeResponse', params: getReorderEventParams },
            { type: 'setReordering', params: { reordering: false } },
            { type: 'setPreviousStep', params: { previousStep: 'locationSearch' } },
          ],
        },
        SET_REORDERING: {
          target: 'locationSearch',
          actions: [
            { type: 'storeOrderDetails', params: getReorderEventParams },
            { type: 'storeResponse', params: getReorderEventParams },
            { type: 'setReordering', params: { reordering: true } },
          ],
        },
        CANCEL_REORDER: {
          target: 'idle',
          actions: 'resetContext',
          guard: {
            type: 'checkIfCanCancelStep',
            params: ({ event }) => ({ requestedStep: event.step, currentStep: 'locationSearch' }),
          },
        },
      },
    },
    confirmingGroupOrderReorder: {
      exit: 'clearGroupOrderReorderRequest',
      after: {
        GROUP_ORDER_REORDER_CONFIRMATION_TIMEOUT: [
          {
            target: 'locationSearch',
            guard: 'checkIfGroupOrderReorderStartedFromLocationSearch',
          },
          {
            target: 'idle',
            actions: 'resetContext',
          },
        ],
      },
      on: {
        CANCEL_GROUP_ORDER_REORDER_CONFIRMATION: [
          {
            target: 'locationSearch',
            guard: 'checkIfGroupOrderReorderStartedFromLocationSearch',
          },
          {
            target: 'idle',
            actions: 'resetContext',
          },
        ],
        CONFIRM_GROUP_ORDER_REORDER: {
          target: 'idle',
        },
      },
    },
    locationConfirmation: {
      on: {
        CHANGE_LOCATION: {
          target: 'locationSearch',
          actions: { type: 'setPreviousStep', params: { previousStep: 'locationConfirmation' } },
        },
        CONFLICT_REVIEW: {
          target: 'conflictReview',
          actions: [
            { type: 'storeResponse', params: getReorderEventParams },
            { type: 'setReordering', params: { reordering: false } },
            { type: 'setPreviousStep', params: { previousStep: 'locationConfirmation' } },
          ],
        },
        SET_REORDERING: {
          target: 'locationConfirmation',
          actions: [
            { type: 'storeOrderDetails', params: getReorderEventParams },
            { type: 'storeResponse', params: getReorderEventParams },
            { type: 'setReordering', params: { reordering: true } },
          ],
        },
        CANCEL_REORDER: {
          target: 'idle',
          actions: 'resetContext',
          guard: {
            type: 'checkIfCanCancelStep',
            params: ({ event }) => ({
              requestedStep: event.step,
              currentStep: 'locationConfirmation',
            }),
          },
        },
      },
    },
    conflictReview: {
      on: {
        CHANGE_LOCATION: {
          target: 'locationSearch',
          actions: { type: 'setPreviousStep', params: { previousStep: 'conflictReview' } },
        },
        FINISH_REORDER: {
          target: 'idle',
          actions: 'resetContext',
        },
        CANCEL_REORDER: {
          target: 'idle',
          actions: 'resetContext',
          guard: {
            type: 'checkIfCanCancelStep',
            params: ({ event }) => ({ requestedStep: event.step, currentStep: 'conflictReview' }),
          },
        },
      },
    },
    activeBagWarning: {
      on: {
        CONFIRM_LOCATION: {
          target: 'locationConfirmation',
          actions: { type: 'setPreviousStep', params: { previousStep: 'activeBagWarning' } },
        },
        CANCEL_REORDER: {
          target: 'idle',
          actions: 'resetContext',
          guard: {
            type: 'checkIfCanCancelStep',
            params: ({ event }) => ({ requestedStep: event.step, currentStep: 'activeBagWarning' }),
          },
        },
      },
    },
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

export type ReorderMachineActorRef = ActorRefFrom<typeof reorderMachine>;
